<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

defineProps({
  isOpen: { type: Boolean, default: false },
  isSending: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'send']);

const { t } = useI18n();

const emailInput = ref('');
const emailList = ref([]);
const error = ref('');

function addEmail() {
  const val = emailInput.value.trim();
  if (!val.includes('@')) {
    error.value = 'Enter a valid email address';
    return;
  }
  if (!emailList.value.includes(val)) emailList.value.push(val);
  emailInput.value = '';
  error.value = '';
}

function removeEmail(email) {
  emailList.value = emailList.value.filter(e => e !== email);
}

function handleSend() {
  if (emailList.value.length === 0) {
    error.value = 'Add at least one recipient';
    return;
  }
  emit('send', [...emailList.value]);
}
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    @click.self="$emit('close')"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 mx-4">
      <h2 class="text-lg font-bold text-[#1B2A4A] mb-1">
        {{ t('MINISTRY.REPORT.EMAIL_MODAL.TITLE') }}
      </h2>
      <p class="text-sm text-slate-500 mb-4">
        {{ t('MINISTRY.REPORT.EMAIL_MODAL.SUBTITLE') }}
      </p>

      <div class="flex gap-2 mb-1">
        <input
          v-model="emailInput"
          type="email"
          :placeholder="t('MINISTRY.REPORT.EMAIL_MODAL.EMAIL_PLACEHOLDER')"
          class="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A84C] focus:border-transparent"
          @keydown.enter.prevent="addEmail"
        />
        <button
          class="bg-[#1B2A4A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1B2A4A]/90 transition-colors"
          @click="addEmail"
        >
          {{ t('MINISTRY.REPORT.EMAIL_MODAL.ADD') }}
        </button>
      </div>
      <p v-if="error" class="text-red-500 text-xs mb-2">{{ error }}</p>

      <div
        v-if="emailList.length"
        class="flex flex-wrap gap-2 mb-4 min-h-[32px]"
      >
        <span
          v-for="email in emailList"
          :key="email"
          class="bg-[#1B2A4A]/10 text-[#1B2A4A] text-xs rounded-full px-3 py-1 flex items-center gap-1.5"
        >
          {{ email }}
          <button
            class="text-[#1B2A4A]/50 hover:text-red-500 transition-colors leading-none"
            @click="removeEmail(email)"
          >
            <span class="i-lucide-x size-3" />
          </button>
        </span>
      </div>
      <div v-else class="mb-4 min-h-[32px]" />

      <div class="flex justify-end gap-3 pt-2 border-t border-slate-100">
        <button
          class="text-slate-500 text-sm hover:text-slate-700 font-medium transition-colors"
          @click="$emit('close')"
        >
          {{ t('MINISTRY.REPORT.EMAIL_MODAL.CANCEL') }}
        </button>
        <button
          :disabled="isSending"
          class="bg-[#C9A84C] text-white px-5 py-2 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
          @click="handleSend"
        >
          {{
            isSending
              ? t('MINISTRY.REPORT.EMAIL_MODAL.SENDING')
              : t('MINISTRY.REPORT.EMAIL_MODAL.SEND')
          }}
        </button>
      </div>
    </div>
  </div>
</template>
